import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, LucideProps } from "lucide-react";
import { ForwardRefExoticComponent, RefAttributes, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { NavigationItem } from "@/components/layout/components/NavigationItem";
import { type NavigationAnchor } from "@/components/layout/types";

interface NavigationItemWithSubmenu {
  title: string;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  open?: boolean;
  items: NavigationAnchor[];
  onOpenChange: (isOpen: boolean) => void;
}

export const NavigationItemWithSubmenu = ({
  title,
  icon,
  items,
  open,
  onOpenChange,
}: NavigationItemWithSubmenu) => {
  const Icon = icon;
  const sidebar = useSidebar();
  const location = useLocation();

  const onCloseAutoFocus = useCallback((event: FocusEvent) => {
    event.preventDefault();
  }, []);

  if (items.length === 0) return null;
  return (
    <SidebarMenu>
      <Collapsible
        className="group/collapsible"
        open={open}
        onOpenChange={onOpenChange}
      >
        <DropdownMenu>
          <SidebarMenuItem>
            {sidebar.open && (
              <CollapsibleTrigger asChild>
                <SidebarMenuButton>
                  {Icon && <Icon />}
                  {title}
                  <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
            )}
            {!sidebar.open && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton>
                      {Icon && <Icon />}
                      {title}
                      <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">{title}</TooltipContent>
              </Tooltip>
            )}
          </SidebarMenuItem>
          <DropdownMenuContent side="right" onCloseAutoFocus={onCloseAutoFocus}>
            {items.map((item) => (
              <DropdownMenuItem key={item.title} asChild className="gap-2">
                <NavigationItem item={item} iconClassName="w-4 h-4" />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <CollapsibleContent>
          <SidebarMenuSub>
            {items.map((item) => (
              <SidebarMenuSubItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={item.url === location.pathname}
                >
                  <NavigationItem item={item} />
                </SidebarMenuButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenu>
  );
};
